import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

const BITFINEX_REST_BASE = 'https://api-pub.bitfinex.com';
const BITFINEX_WS_URL = 'wss://api-pub.bitfinex.com/ws/2';
const BITFINEX_BOOK_DEPTH = 25;
const BITFINEX_MAX_CANDLES = 10_000;
const BITFINEX_MAX_TRADES = 10_000;
const BITFINEX_SIGNIFICANT_PRICE_DIGITS = 5;
const BITFINEX_MAX_PRICE_DECIMALS = 8;

export const bitfinexRestClient = proxyRestOptions.client!;

type BitfinexNumber = number | string;
type BitfinexTicker = BitfinexNumber[];
export type BitfinexBookRow = [
  price: BitfinexNumber,
  count: BitfinexNumber,
  amount: BitfinexNumber,
];
export type BitfinexCandle = [
  timestamp: BitfinexNumber,
  open: BitfinexNumber,
  close: BitfinexNumber,
  high: BitfinexNumber,
  low: BitfinexNumber,
  volume: BitfinexNumber,
];
type BitfinexTrade = [
  id: BitfinexNumber,
  timestamp: BitfinexNumber,
  amount: BitfinexNumber,
  price: BitfinexNumber,
];

type BitfinexPublicChannel = 'book' | 'ticker' | 'trades' | 'candles';

interface BitfinexControlMessage {
  event?: string;
  channel?: string;
  chanId?: number;
  symbol?: string;
  key?: string;
  code?: number;
  msg?: string;
}

export interface BitfinexOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
}

const BITFINEX_TIMEFRAMES: Partial<Record<IChartTimeframe, string>> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '6h': '6h',
  '12h': '12h',
  '24h': '1D',
  '1d': '1D',
  '1w': '1W',
  '1M': '1M',
};

const isFiniteNumber = (value: unknown) =>
  value !== null &&
  value !== undefined &&
  (typeof value !== 'string' || value.trim() !== '') &&
  Number.isFinite(Number(value));

const asString = (value: BitfinexNumber) => String(value);

const asBitfinexTrade = (value: unknown): BitfinexTrade | undefined => {
  if (
    !Array.isArray(value) ||
    value.length < 4 ||
    !value.slice(0, 4).every(isFiniteNumber) ||
    Number(value[2]) === 0
  ) {
    return undefined;
  }
  return value.slice(0, 4) as BitfinexTrade;
};

const timeframeFromInterval = (interval: number): string => {
  const intervalToTimeframe: Record<number, string> = {
    60_000: '1m',
    300_000: '5m',
    900_000: '15m',
    1_800_000: '30m',
    3_600_000: '1h',
    10_800_000: '3h',
    21_600_000: '6h',
    43_200_000: '12h',
    86_400_000: '1D',
    604_800_000: '1W',
  };
  return intervalToTimeframe[interval] ?? '1m';
};

export const buildBitfinexSymbol = (base: string, quote: string) =>
  `t${base.toUpperCase()}${quote.toUpperCase()}`;

export const bitfinexPrecisionFromPrice = (
  priceValue: unknown,
): { precision: number; tick: string } => {
  if (!isFiniteNumber(priceValue) || Number(priceValue) <= 0) {
    return { precision: 1, tick: '0.1' };
  }

  const exponent = Math.floor(Math.log10(Math.abs(Number(priceValue))));
  const tickExponent = Math.max(
    -BITFINEX_MAX_PRICE_DECIMALS,
    exponent - (BITFINEX_SIGNIFICANT_PRICE_DIGITS - 1),
  );
  if (tickExponent >= 0) {
    return { precision: 0, tick: String(10 ** tickExponent) };
  }

  const precision = -tickExponent;
  return { precision, tick: (10 ** tickExponent).toFixed(precision) };
};

export const normalizeBitfinexCandles = (candles: BitfinexCandle[]): KLine[] =>
  candles
    .filter(
      candle =>
        Array.isArray(candle) && candle.length >= 6 && candle.slice(0, 6).every(isFiniteNumber),
    )
    .map(
      ([timestamp, open, close, high, low, volume]) =>
        [
          Number(timestamp),
          asString(open),
          asString(high),
          asString(low),
          asString(close),
          asString(volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const createBitfinexOrderbookState = (): BitfinexOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
});

export const applyBitfinexOrderbookUpdate = (
  state: BitfinexOrderbookState,
  row: BitfinexBookRow,
) => {
  if (
    !Array.isArray(row) ||
    row.length < 3 ||
    !row.slice(0, 3).every(isFiniteNumber) ||
    Number(row[0]) <= 0
  ) {
    throw new Error('Bitfinex public orderbook row is invalid');
  }

  const price = asString(row[0]);
  const count = Number(row[1]);
  const amount = Number(row[2]);
  const side = amount > 0 ? state.bids : state.asks;
  const oppositeSide = amount > 0 ? state.asks : state.bids;

  if (count === 0) {
    side.delete(price);
    return;
  }
  if (count < 0 || amount === 0) {
    throw new Error('Bitfinex public orderbook row has invalid count or amount');
  }

  side.set(price, normalizeNumber(Math.abs(amount)));
  oppositeSide.delete(price);
};

export const applyBitfinexOrderbookSnapshot = (
  state: BitfinexOrderbookState,
  rows: BitfinexBookRow[],
) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error('Bitfinex public orderbook snapshot is empty');
  }

  state.bids.clear();
  state.asks.clear();
  rows.forEach(row => applyBitfinexOrderbookUpdate(state, row));
  if (state.bids.size === 0 || state.asks.size === 0) {
    throw new Error('Bitfinex public orderbook snapshot is one-sided');
  }
};

export const buildBitfinexSubscriptions = (symbol: string, timeframe: string) => [
  {
    event: 'subscribe' as const,
    channel: 'book' as const,
    symbol,
    prec: 'P0',
    freq: 'F0',
    len: '25',
  },
  { event: 'subscribe' as const, channel: 'ticker' as const, symbol },
  { event: 'subscribe' as const, channel: 'trades' as const, symbol },
  {
    event: 'subscribe' as const,
    channel: 'candles' as const,
    key: `trade:${timeframe}:${symbol}`,
  },
];

export class BitfinexStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  orderbook = createBitfinexOrderbookState();
  channels = new Map<number, BitfinexPublicChannel>();

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildBitfinexSymbol(base, quote);
    this.displayDepth = Math.min(
      BITFINEX_BOOK_DEPTH,
      Math.max(1, orderbookDepth ?? BITFINEX_BOOK_DEPTH),
    );
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const bids = [...this.orderbook.bids.entries()]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.displayDepth);
    const asks = [...this.orderbook.asks.entries()]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.displayDepth);
    this.onOrderbookUpdate({ bids, asks });
  };

  publishTicker = (ticker: BitfinexTicker) => {
    if (
      !Array.isArray(ticker) ||
      ticker.length < 10 ||
      !ticker.slice(0, 10).every(isFiniteNumber)
    ) {
      throw new Error(`Bitfinex public ticker ${this.pair} has an invalid shape`);
    }

    const last = asString(ticker[6]);
    const baseVolume = asString(ticker[7]);
    const precision = bitfinexPrecisionFromPrice(ticker[6]);
    this.precision = precision.precision;
    this.selectedPrecision = precision.precision;
    this.priceTick = precision.tick;
    this.precisionUpdate(precision.precision);
    this.onTickerUpdate({
      baseVolume,
      change: normalizeNumber(Number(ticker[5]) * 100),
      close: last,
      high: asString(ticker[8]),
      last,
      low: asString(ticker[9]),
      quoteVolume: normalizeNumber(Number(ticker[7]) * Number(ticker[6])),
    });
  };

  publishTrade = (tradeValue: unknown) => {
    const trade = asBitfinexTrade(tradeValue);
    if (!trade) {
      throw new Error(`Bitfinex public trade ${this.pair} has an invalid shape`);
    }
    const amount = Number(trade[2]);
    this.onTradeUpdate({
      price: asString(trade[3]),
      quantity: normalizeNumber(Math.abs(amount)),
      timestamp: asString(trade[1]),
      side: amount > 0 ? 'buy' : 'sell',
    });
  };

  publishCandle = (candleValue: unknown) => {
    if (!Array.isArray(candleValue)) {
      throw new Error(`Bitfinex public candle ${this.pair} has an invalid shape`);
    }
    const [candle] = normalizeBitfinexCandles([candleValue as BitfinexCandle]);
    if (!candle) {
      throw new Error(`Bitfinex public candle ${this.pair} has an invalid shape`);
    }
    this.klineUpdate(candle);
  };

  fetchTicker = async () => {
    try {
      const response = await bitfinexRestClient.get<BitfinexTicker>(
        `${BITFINEX_REST_BASE}/v2/ticker/${this.pair}`,
      );
      this.publishTicker(response.data);
    } catch (error) {
      console.log('Bitfinex ticker error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await bitfinexRestClient.get<BitfinexBookRow[]>(
        `${BITFINEX_REST_BASE}/v2/book/${this.pair}/P0`,
        { params: { len: BITFINEX_BOOK_DEPTH } },
      );
      applyBitfinexOrderbookSnapshot(this.orderbook, response.data);
      this.publishOrderbook();
    } catch (error) {
      console.log('Bitfinex orderbook error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const bitfinexInterval = BITFINEX_TIMEFRAMES[interval];
    if (!bitfinexInterval) return [];
    const boundedLimit = Math.min(BITFINEX_MAX_CANDLES, Math.max(1, limit));
    const params: Record<string, number> = { limit: boundedLimit, sort: -1 };
    if (from > 0) params.start = from;
    if (to > 0) params.end = to;

    try {
      const response = await bitfinexRestClient.get<BitfinexCandle[]>(
        `${BITFINEX_REST_BASE}/v2/candles/trade:${bitfinexInterval}:${this.pair}/hist`,
        { params },
      );
      return normalizeBitfinexCandles(response.data ?? []);
    } catch (error) {
      console.log('Bitfinex klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    const boundedLimit = Math.min(BITFINEX_MAX_TRADES, Math.max(1, limit));
    try {
      const response = await bitfinexRestClient.get<BitfinexTrade[]>(
        `${BITFINEX_REST_BASE}/v2/trades/${this.pair}/hist`,
        { params: { limit: boundedLimit, sort: -1 } },
      );
      return (response.data ?? []).flatMap(tradeValue => {
        const trade = asBitfinexTrade(tradeValue);
        if (!trade) return [];
        const amount = Number(trade[2]);
        return [
          {
            price: asString(trade[3]),
            quantity: normalizeNumber(Math.abs(amount)),
            timestamp: asString(trade[1]),
            side: amount > 0 ? 'buy' : 'sell',
          },
        ];
      });
    } catch (error) {
      console.log('Bitfinex trades error:', error);
      return [];
    }
  };

  handleControlMessage = (message: BitfinexControlMessage) => {
    if (message.event === 'error') {
      throw new Error(
        `Bitfinex WebSocket error ${String(message.code ?? '')}: ${message.msg ?? ''}`,
      );
    }
    if (message.event !== 'subscribed') return;
    if (
      !Number.isInteger(message.chanId) ||
      !['book', 'ticker', 'trades', 'candles'].includes(message.channel ?? '')
    ) {
      throw new Error('Bitfinex WebSocket subscription response is invalid');
    }
    this.channels.set(message.chanId!, message.channel as BitfinexPublicChannel);
  };

  handleBookMessage = (payload: unknown) => {
    if (!Array.isArray(payload)) {
      throw new Error('Bitfinex WebSocket book payload is invalid');
    }
    if (Array.isArray(payload[0])) {
      applyBitfinexOrderbookSnapshot(this.orderbook, payload as BitfinexBookRow[]);
    } else {
      applyBitfinexOrderbookUpdate(this.orderbook, payload as BitfinexBookRow);
    }
    this.publishOrderbook();
  };

  handleWebSocketMessage = (message: unknown) => {
    if (!Array.isArray(message)) {
      this.handleControlMessage(message as BitfinexControlMessage);
      return;
    }
    if (!Number.isInteger(message[0]) || message[1] === 'hb') return;

    const channel = this.channels.get(Number(message[0]));
    if (!channel) return;
    if (channel === 'book') {
      this.handleBookMessage(message[1]);
      return;
    }
    if (channel === 'ticker') {
      this.publishTicker(message[1] as BitfinexTicker);
      return;
    }
    if (channel === 'trades') {
      if (message[1] === 'tu') this.publishTrade(message[2]);
      return;
    }
    if (channel === 'candles') {
      const payload = message[1];
      if (Array.isArray(payload) && Array.isArray(payload[0])) {
        const latest = normalizeBitfinexCandles(payload as BitfinexCandle[]).at(-1);
        if (latest) this.klineUpdate(latest);
      } else {
        this.publishCandle(payload);
      }
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    const timeframe = timeframeFromInterval(interval);
    const ws = new ReconnectingWebSocket(BITFINEX_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;
    ws.onopen = () => {
      this.channels.clear();
      this.orderbook.bids.clear();
      this.orderbook.asks.clear();
      buildBitfinexSubscriptions(this.pair, timeframe).forEach(subscription => {
        ws.send(JSON.stringify(subscription));
      });
    };
    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        this.handleWebSocketMessage(JSON.parse(event.data));
      } catch (error) {
        console.log('Bitfinex websocket error:', error);
        void this.fetchOrderbook();
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
    this.channels.clear();
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = Math.min(BITFINEX_BOOK_DEPTH, Math.max(1, depth));
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchTicker(), this.fetchOrderbook()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new BitfinexStrategy(args);
    await bootstrapStrategy(strategy);
    return strategy;
  };
}
